import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { usePriceRules } from "../hooks";
import { useProducts } from "../../products/hooks";
import { useUsers } from "../../users/hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { cleanObject } from "../../../helpers/clean-object";

interface ISearchForm {
  productId?: string;
  customerId?: string;
  createdById?: string;
}

export const PriceRules: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: productsData } = useProducts({ where: {}, limit: 100, offset: 0 });
  const { data: usersData } = useUsers({ where: {}, limit: 100, offset: 0 });

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      productId: searchParams.get("productId") ?? "",
      customerId: searchParams.get("customerId") ?? "",
      createdById: searchParams.get("createdById") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/price-rules",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const productId = searchParams.get("productId");
  const customerId = searchParams.get("customerId");
  const createdById = searchParams.get("createdById");

  const where: any = {};
  if (productId) where.productId = +productId;
  if (customerId) where.customerId = +customerId;
  if (createdById) where.createdById = +createdById;

  const { data, loading } = usePriceRules({ where });

  const productName = (id: number | null) =>
    productsData?.products?.results?.find((product) => product.id === id)?.name ?? `#${id}`;

  return (
    <>
      <Header
        title="Règles de prix"
        subtitle="Liste des règles de prix"
        buttons={[
          {
            actionText: "Nouvelle règle",
            bgColor: "indigo",
            textColor: "white",
            link: `/price-rule/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder=""
          advancedFilters={
            <>
              <Select label="Produit" className="w-full" {...form.register("productId")}>
                <option value="">Tous</option>
                {productsData?.products?.results?.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Select>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Créé par" className="w-full" {...form.register("createdById")}>
                <option value="">Tous</option>
                {usersData?.users?.results?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname}
                  </option>
                ))}
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.priceRules?.results?.length ? (
            <EmptyList text="Aucune règle de prix" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Produit</TableCell>
                  <TableCell header align="right">
                    Quantité min.
                  </TableCell>
                  <TableCell header align="right">
                    Prix
                  </TableCell>
                  <TableCell header>Créé par</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.priceRules?.results?.map((priceRule) => (
                  <TableRow
                    key={`price-rule-${priceRule.id}`}
                    onClick={() => navigate(`/price-rule/${priceRule.id}/update`)}
                  >
                    <TableCell>{productName(priceRule.productId)}</TableCell>
                    <TableCell align="right">{priceRule.quantity ?? 1}</TableCell>
                    <TableCell align="right">
                      {((priceRule.priceCents ?? 0) / 100).toFixed(2)} €
                    </TableCell>
                    <TableCell>
                      {priceRule.createdBy
                        ? `${priceRule.createdBy.firstname} ${priceRule.createdBy.lastname}`
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
